import * as UI from '@brewlogix/blx-ui'
import mapboxgl from 'mapbox-gl'
import React from 'react'
import { useFilterStore } from '../../state/filterStore'
import { mapStore } from '../../state/mapStore'
import { radiusOptions } from '../../utils/constants'
import { routeDistanceOptions } from '../../utils/constants/routeDistanceOptions'
import useMapFunctions from '../../utils/functions/useMapFunctions'
import TurnByTurnDirections from '../routes/TurnByTurnDirections'
import ProfileButton from './ProfileButton'
import SearchAsMapMoves from './SearchAsMapMoves'

interface IMap {
    mapRef: React.RefObject<mapboxgl.Map>
}
const Map: React.FC<IMap> = ({ mapRef }) => {
    // Map Store
    const setFullScreenShowFullMap = mapStore((state) => state.setFullScreenShowFullMap)
    const fullScreenShowFullMap = mapStore((state) => state.fullScreenShowFullMap)
    const userLocationState = mapStore((state) => state.userLocationState)
    const setProfile = mapStore((state) => state.setProfile)
    const radius = mapStore((state) => state.radius)
    const setRadius = mapStore((state) => state.setRadius)
    const activeTab = mapStore((state) => state.activeTab)
    const setSearchAsMapMoves = mapStore((state) => state.setSearchAsMapMoves)
    const setFallbackRadius = mapStore((state) => state.setFallbackRadius)
    const directionLegs = mapStore((state) => state.directionLegs)
    const directionCoordinates = mapStore((state) => state.directionCoordinates)
    const distanceFromRouteSelection = mapStore((state) => state.distanceFromRouteSelection)
    const setDistanceFromRouteSelection = mapStore((state) => state.setDistanceFromRouteSelection)

    // Filter Store
    const stateFilter = useFilterStore((state) => state.stateFilter)
    const setStateFilter = useFilterStore((state) => state.setStateFilter)
    const clearBorders = useFilterStore((state) => state.clearBorders)
    const setClearBorders = useFilterStore((state) => state.setClearBorders)
    const setFallBackStateFilter = useFilterStore((state) => state.setFallBackStateFilter)
    const setFilterCount = useFilterStore((state) => state.setFilterCount)

    const { map } = useMapFunctions()

    interface IRadiusOption {
        label: string
        value: number
    }

    const handleCenterLocationClick = () => {
        map.current.setZoom(12)
        map.current.setCenter([userLocationState?.lng, userLocationState?.lat])
    }
    const handleRadiusChange = (e: IRadiusOption) => {
        setSearchAsMapMoves(false)
        setFallbackRadius({ label: `${e.label}`, value: e.value })
        setRadius({
            label: `${e.label}`,
            value: e.value,
        })
    }

    const handleRemoveBoundaryClick = () => {
        setFallBackStateFilter(stateFilter)
        setStateFilter({ label: 'All', value: 0 })
        mapRef.current?.setLayoutProperty('state-border-click', 'visibility', 'none')
        mapRef.current?.setLayoutProperty('state-borders', 'visibility', 'visible')
        setClearBorders(!clearBorders)
        setFilterCount()
    }
    const showRemoveBoundaryButton = stateFilter.value !== 0
    const handleDistanceFromRouteSelectionChange = (e: { label: string; value: number }) => {
        setDistanceFromRouteSelection(e)
    }

    return (
        <>
            <UI.Button
                theme='link'
                size='sm'
                customClasses='absolute z-10 drop-shadow-xl top-6 left-6 text-dark bg-light h-10 w-10 p-0 hidden xl:block'
                onClick={() => setFullScreenShowFullMap(!fullScreenShowFullMap)}
                aria-label={fullScreenShowFullMap ? 'Collapse map' : 'Expand map'}
            >
                <UI.Icon
                    slug={fullScreenShowFullMap ? 'DoubleChevronRight' : 'DoubleChevronLeft'}
                    theme='link'
                    size='sm'
                    customClasses='mt-[0.2rem] '
                />
            </UI.Button>
            <UI.Button
                size='sm'
                theme='light'
                customClasses='z-10 absolute drop-shadow-xl bottom-6 right-6 h-10 w-10 p-0 flex items-center justify-center button--user-location'
                onClick={handleCenterLocationClick}
                aria-label='Set map and zoom to User Location'
            >
                <UI.Icon slug='ArrowPointer' size='sm' />
            </UI.Button>
            <div className='absolute left-1/2 top-6 z-0 flex h-10 w-max -translate-x-1/2 transform rounded-md bg-light drop-shadow-xl md:left-6 md:transform-none xl:left-1/2 xl:transform'>
                <ProfileButton
                    label='Driving'
                    iconSlug='Car'
                    onClick={() => setProfile('driving')}
                />
                <ProfileButton
                    label='Walking'
                    iconSlug='Walk'
                    onClick={() => setProfile('walking')}
                />
                <ProfileButton
                    label='Cycling'
                    iconSlug='Bike'
                    onClick={() => setProfile('cycling')}
                />

                {activeTab === 'routes' ? (
                    <UI.DynamicSelect
                        id='select-ditance-from-route'
                        instanceId='select-ditance-from-route'
                        label=''
                        options={routeDistanceOptions}
                        value={distanceFromRouteSelection}
                        customWrapperClasses='select--radius'
                        onChange={(e) => handleDistanceFromRouteSelectionChange(e)}
                        aria-label='Select distance from route'
                        disabled={directionCoordinates.length < 2}
                    />
                ) : (
                    <UI.DynamicSelect
                        id='select-radius'
                        instanceId='select-radius'
                        label=''
                        options={radiusOptions}
                        value={radius}
                        customWrapperClasses='select--radius'
                        onChange={(e) => handleRadiusChange(e)}
                        aria-label='Select radius'
                    />
                )}
            </div>
            {directionLegs?.length > 0 &&
                directionCoordinates.length > 1 &&
                activeTab === 'routes' && (
                    <TurnByTurnDirections customClasses='hidden absolute top-20 bg-white z-10 lg:flex h-fit max-h-[calc(100%-6rem)] w-64 flex-col overflow-y-scroll rounded-md   p-4 md:left-6' />
                )}
            {showRemoveBoundaryButton && (
                <div className=' absolute bottom-20 left-1/2 z-10 flex w-full -translate-x-1/2 transform justify-center md:bottom-6 md:w-auto'>
                    <UI.Button
                        size='sm'
                        theme='success'
                        customClasses='z-10 drop-shadow-xl px-2 py-1 flex items-center justify-center font-normal rounded-full font-[lato]'
                        onClick={handleRemoveBoundaryClick}
                        label='Remove state boundary'
                        icon='HeroIcons.XCircle'
                    />
                </div>
            )}
            <div className='absolute bottom-6 left-1/2 z-10 flex h-10 -translate-x-1/2 transform rounded-md bg-light drop-shadow-xl md:bottom-auto md:left-auto md:right-6 md:top-6 md:transform-none'>
                <div className='flex w-full cursor-pointer items-center px-2 pt-0 font-[lato]'>
                    <SearchAsMapMoves tooltipLabel='Search as map moves is not available when using BreweryRoutes' />
                </div>
            </div>
        </>
    )
}

export default React.memo(Map)
